import { callApi } from '../utils';

/**
 * Gmail Integration Service
 *
 * Wraps the /integrations/gmail/* endpoints used to register Google OAuth
 * email accounts and manage their sync status from the admin dashboard.
 */

/**
 * Generate the Google OAuth consent-screen URL.
 * The caller should redirect the admin user to the returned URL.
 *
 * GET /integrations/gmail/sync
 * @returns {Promise<{ url: string }>}
 */
export const getConsentUrl = () => callApi('/integrations/gmail/sync');

/**
 * Complete the OAuth flow by forwarding the `code` Google sent back.
 *
 * POST /integrations/gmail/
 * @param {string} code – the authorization code from Google's redirect.
 * @returns {Promise<{ message: string }>}
 */
export const registerEmail = (code) =>
  callApi('/integrations/gmail/', { code }, 'post');

/**
 * Fetch the list of registered Gmail accounts with pagination.
 *
 * GET /integrations/gmail/?limit=…&offset=…&order=…
 * @param {{ limit?: number, offset?: number, order?: string }} params
 * @returns {Promise<{ status: string, data: Array, meta: { count: number } }>}
 */
export const getAccounts = (params = {}) =>
  callApi('/integrations/gmail', params);

/**
 * Toggle the active/inactive sync state for a registered email address.
 *
 * PUT /integrations/gmail/
 * @param {string} emailAddress
 * @param {boolean} isActive
 * @returns {Promise<{ email: string, is_active: boolean }>}
 */
export const toggleAccountStatus = (emailAddress, isActive) =>
  callApi('/integrations/gmail/', { emailAddress, isActive }, 'put');

/**
 * Update the whitelisted senders for a registered email address.
 *
 * PUT /integrations/gmail/
 * @param {string} emailAddress
 * @param {string[]} whitelistedSenders – array of email addresses
 * @returns {Promise<{ message: string, data: object }>}
 */
export const updateWhitelistedSenders = (emailAddress, whitelistedSenders) =>
  callApi(
    '/integrations/gmail/',
    { emailAddress, whitelistedSenders, type: 'whitelisted_senders' },
    'put'
  );

export default {
  getConsentUrl,
  registerEmail,
  getAccounts,
  toggleAccountStatus,
  updateWhitelistedSenders,
};
